import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Loader2, Trash2, Pencil, Crown, Check } from "lucide-react";
import { Button } from "../ui/button";
import { membershipPlansApi } from "../../lib/api";
import { MEMBERSHIP_TERMS, termLabel } from "../../lib/membership";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-50 transition-colors";

const SWATCHES = ["#7C3AED", "#F59E0B", "#94A3B8", "#2563EB", "#059669", "#DC2626", "#0EA5E9", "#DB2777"];

export default function MembershipsTab({ isAdmin }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan | "new" | null

  const reload = async () => {
    setLoading(true);
    try {
      setPlans(await membershipPlansApi.list());
    } catch {
      toast.error("Failed to load membership plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  return (
    <div className="space-y-4" data-testid="memberships-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 bg-white rounded-lg px-3 py-2.5 border border-slate-200 md:bg-transparent md:border-0 md:px-0 md:py-0 md:rounded-none">
        <div className="text-[12.5px] text-slate-500">
          Define tiers customers can buy. Members get the tier discount on services &amp; materials automatically.
        </div>
        {isAdmin && (
          <Button
            onClick={() => setEditing("new")}
            data-testid="membership-new-btn"
            className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold shrink-0"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New Plan
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : plans.length === 0 ? (
        <div className="py-16 px-4 text-center text-slate-400 italic bg-white rounded-xl border border-slate-200">
          No membership plans yet. {isAdmin && "Create one to start offering memberships."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {plans.map((p) => (
            <button
              key={p.id}
              onClick={() => isAdmin && setEditing(p)}
              data-testid={`membership-card-${p.id}`}
              className={`text-left bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-all ${isAdmin ? "cursor-pointer" : "cursor-default"} ${!p.active ? "opacity-60" : ""}`}
            >
              <div className="px-4 py-3 flex items-center gap-2.5" style={{ background: `${p.color}14` }}>
                <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: p.color }}>
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900 text-[15px] truncate">{p.name}</div>
                  <div className="text-[11.5px] font-semibold" style={{ color: p.color }}>{p.discountPct}% member discount</div>
                </div>
                {!p.active && <span className="text-[10px] font-bold uppercase text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Inactive</span>}
              </div>
              <div className="px-4 py-3 space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Pricing</div>
                <div className="flex flex-wrap gap-1.5">
                  {MEMBERSHIP_TERMS.filter((t) => p.termPrices?.[t] > 0).map((t) => (
                    <span key={t} className="text-[11.5px] bg-slate-100 text-slate-700 rounded-md px-2 py-0.5">
                      <span className="text-slate-500">{termLabel(t)}:</span> <strong>{money(p.termPrices[t])}</strong>
                    </span>
                  ))}
                  {MEMBERSHIP_TERMS.filter((t) => p.termPrices?.[t] > 0).length === 0 && (
                    <span className="text-[11.5px] text-slate-400 italic">No prices set</span>
                  )}
                </div>
                {(p.benefits || []).length > 0 && (
                  <ul className="pt-1 space-y-0.5">
                    {p.benefits.slice(0, 4).map((b, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11.5px] text-slate-600">
                        <Check className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {editing && (
        <PlanFormDialog
          plan={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved, deletedId) => {
            if (deletedId) setPlans((prev) => prev.filter((x) => x.id !== deletedId));
            else setPlans((prev) => (prev.some((x) => x.id === saved.id) ? prev.map((x) => (x.id === saved.id ? saved : x)) : [...prev, saved]));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function PlanFormDialog({ plan, onClose, onSaved }) {
  const isNew = !plan;
  const [form, setForm] = useState(
    plan || { name: "", discountPct: 10, color: "#7C3AED", benefits: [], termPrices: {}, active: true },
  );
  const [benefitsText, setBenefitsText] = useState((plan?.benefits || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setTerm = (term, val) =>
    setForm((f) => ({ ...f, termPrices: { ...f.termPrices, [term]: val } }));

  const submit = async () => {
    if (!form.name.trim()) { setError("Plan name is required"); return; }
    setError("");
    setBusy(true);
    const termPrices = {};
    MEMBERSHIP_TERMS.forEach((t) => {
      const v = Number(form.termPrices[t]);
      if (v > 0) termPrices[t] = v;
    });
    const payload = {
      name: form.name.trim(),
      discountPct: Number(form.discountPct) || 0,
      color: form.color,
      benefits: benefitsText.split("\n").map((b) => b.trim()).filter(Boolean),
      termPrices,
      active: !!form.active,
    };
    try {
      const saved = isNew
        ? await membershipPlansApi.create(payload)
        : await membershipPlansApi.update(plan.id, payload);
      toast.success(`${saved.name} ${isNew ? "created" : "updated"}`);
      onSaved(saved);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete the ${plan.name} plan?`)) return;
    setBusy(true);
    try {
      await membershipPlansApi.remove(plan.id);
      toast.success(`${plan.name} deleted`);
      onSaved(null, plan.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="membership-plan-dialog">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div className="text-[14px] font-semibold text-slate-900">{isNew ? "New Membership Plan" : `Edit — ${plan.name}`}</div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Lbl>Plan name</Lbl>
              <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Platinum" data-testid="plan-name" className={inputClass} />
            </div>
            <div>
              <Lbl>Member discount (%)</Lbl>
              <input type="number" min={0} max={100} step={0.5} value={form.discountPct} onChange={(e) => set({ discountPct: e.target.value })} data-testid="plan-discount" className={inputClass} />
            </div>
            <div>
              <Lbl>Active</Lbl>
              <label className="flex items-center gap-2 h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] cursor-pointer">
                <input type="checkbox" checked={!!form.active} onChange={(e) => set({ active: e.target.checked })} data-testid="plan-active" className="accent-blue-600" />
                <span className="text-slate-700">{form.active ? "Available to sell" : "Hidden"}</span>
              </label>
            </div>
          </div>

          <div>
            <Lbl>Badge color</Lbl>
            <div className="flex gap-1.5">
              {SWATCHES.map((c) => (
                <button key={c} onClick={() => set({ color: c })} data-testid={`plan-color-${c}`} className={`h-7 w-7 rounded-full border-2 transition-all ${form.color === c ? "border-slate-900 scale-110" : "border-white shadow"}`} style={{ background: c }} />
              ))}
            </div>
          </div>

          <div>
            <Lbl>Term pricing (leave blank for terms you don't offer)</Lbl>
            <div className="grid grid-cols-3 gap-2">
              {MEMBERSHIP_TERMS.map((t) => (
                <div key={t}>
                  <div className="text-[11px] text-slate-500 mb-0.5">{termLabel(t)}</div>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">$</span>
                    <input
                      type="number" min={0} step={1}
                      value={form.termPrices[t] ?? ""}
                      onChange={(e) => setTerm(t, e.target.value)}
                      data-testid={`plan-term-${t}`}
                      className={`${inputClass} pl-6`}
                      placeholder="—"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Lbl>Benefits (one per line)</Lbl>
            <textarea
              rows={4}
              value={benefitsText}
              onChange={(e) => setBenefitsText(e.target.value)}
              placeholder={"15% off all services & parts\nPriority scheduling\nFree annual tune-up"}
              data-testid="plan-benefits"
              className={`${inputClass} h-auto py-2 resize-none`}
            />
          </div>

          {error && <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-between gap-2 bg-slate-50/40 sticky bottom-0">
          {!isNew && (
            <Button variant="outline" onClick={del} disabled={busy} data-testid="plan-delete" className="h-9 text-[13px] text-rose-600 hover:bg-rose-50 hover:border-rose-300 border-slate-200">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.name.trim()} data-testid="plan-submit" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold">
            {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : (isNew ? <><Plus className="h-4 w-4 mr-1.5" /> Create</> : <><Pencil className="h-4 w-4 mr-1.5" /> Save</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Lbl({ children }) {
  return <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{children}</label>;
}
